/**
 * BulkTransactionTable Component
 *
 * 거래 일괄 입력(A-7)의 테이블 표현 — 넓은 화면(≥768px)에서 사용.
 *
 * @why 이 기능의 동기는 "PC/태블릿에서 엑셀처럼 빠르게 여러 건 입력"이다.
 * 넓은 화면에서는 한 행을 한 줄로 보여주는 편이 훑어보기도, 연속 입력도 빠르다.
 *
 * 공용 Input/Select 대신 네이티브 컨트롤을 직접 쓰는 이유:
 * 공용 컴포넌트는 칸마다 라벨 블록과 48px 높이를 갖도록 만들어져 있어
 * 표의 한 칸에 넣으면 한 행이 화면을 가득 채운다. 표에서는 열 머리글이 라벨 역할을 하므로
 * 컨트롤에는 aria-label만 달아 접근성을 유지한 채 높이를 줄인다.
 *
 * 이 컴포넌트는 상태를 갖지 않는다. 카드 표현과 상태·검증·저장 로직을 100% 공유한다.
 */

import { memo, useMemo } from 'react'
import type {
  BulkRowViewProps,
  BulkTransactionDraftRow,
} from '../types/BulkTransaction'

/** 표 안에서 쓰는 컨트롤 공통 스타일 (행 높이를 낮게 유지) */
const CONTROL_CLASS =
  'w-full h-9 px-2 text-sm rounded-md border bg-white dark:bg-gray-800 dark:text-gray-100 ' +
  'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-primary-500'

const ERROR_CONTROL_CLASS =
  'w-full h-9 px-2 text-sm rounded-md border bg-white dark:bg-gray-800 dark:text-gray-100 ' +
  'border-red-500 focus:ring-2 focus:ring-red-500'

function cellClass(hasError: boolean): string {
  return hasError ? ERROR_CONTROL_CLASS : CONTROL_CLASS
}

function BulkTransactionTableRowBase(props: BulkRowViewProps) {
  const {
    row,
    rowNumber,
    validation,
    expenseCategories,
    incomeCategories,
    paymentMethodOptions,
    userOptions,
    onChange,
    onRemove,
    disabled,
  } = props

  const isIncome = row.type === 'income'
  const majorCategories = isIncome ? incomeCategories : expenseCategories

  const minorCategories = useMemo(() => {
    const major = majorCategories.find((cat) => cat.id === row.majorCategoryId)
    return major?.children || []
  }, [majorCategories, row.majorCategoryId])

  const errors = validation?.errors ?? []
  const errorFields = new Set(errors.map((e) => e.field))
  const has = (field: keyof BulkTransactionDraftRow) => errorFields.has(field)

  const spenderLabel = isIncome ? '받은 사람' : '결제 계좌 소유자'
  const set = (patch: Partial<BulkTransactionDraftRow>) => onChange(row.clientId, patch)

  return (
    <>
      <tr className={errors.length > 0 ? 'bg-red-50/60 dark:bg-red-900/10' : undefined}>
        <td className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 tabular-nums">
          {rowNumber}
        </td>

        <td className="px-2 py-1.5">
          <input
            type="date"
            value={row.date}
            onChange={(e) => set({ date: e.target.value })}
            disabled={disabled}
            aria-label={`${rowNumber}행 날짜`}
            className={cellClass(has('date'))}
          />
        </td>

        <td className="px-2 py-1.5">
          <select
            value={row.type}
            onChange={(e) =>
              set({ type: e.target.value as BulkTransactionDraftRow['type'] })
            }
            disabled={disabled}
            aria-label={`${rowNumber}행 구분`}
            className={CONTROL_CLASS}
          >
            <option value="expense">지출</option>
            <option value="income">수입</option>
          </select>
        </td>

        <td className="px-2 py-1.5">
          <input
            type="text"
            inputMode="numeric"
            value={row.amount}
            onChange={(e) => set({ amount: e.target.value })}
            disabled={disabled}
            placeholder="0"
            aria-label={`${rowNumber}행 금액`}
            className={`${cellClass(has('amount'))} text-right tabular-nums`}
          />
        </td>

        <td className="px-2 py-1.5">
          <select
            value={row.majorCategoryId}
            onChange={(e) => set({ majorCategoryId: e.target.value })}
            disabled={disabled}
            aria-label={`${rowNumber}행 대분류`}
            className={cellClass(has('majorCategoryId'))}
          >
            <option value="">선택</option>
            {majorCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </td>

        <td className="px-2 py-1.5">
          <select
            value={row.minorCategoryId}
            onChange={(e) => set({ minorCategoryId: e.target.value })}
            disabled={disabled || minorCategories.length === 0}
            aria-label={`${rowNumber}행 소분류`}
            className={cellClass(has('minorCategoryId'))}
          >
            <option value="">선택</option>
            {minorCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </td>

        <td className="px-2 py-1.5">
          {/* 수입은 결제수단 개념이 없어 칸을 비활성 표시로 대체한다 */}
          {isIncome ? (
            <span className="block text-center text-sm text-gray-400 dark:text-gray-500">
              —
            </span>
          ) : (
            <select
              value={row.paymentMethodId}
              onChange={(e) => set({ paymentMethodId: e.target.value })}
              disabled={disabled}
              aria-label={`${rowNumber}행 결제수단`}
              className={cellClass(has('paymentMethodId'))}
            >
              <option value="">선택</option>
              {paymentMethodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </td>

        <td className="px-2 py-1.5">
          <select
            value={row.spenderId}
            onChange={(e) => set({ spenderId: e.target.value })}
            disabled={disabled}
            aria-label={`${rowNumber}행 ${spenderLabel}`}
            className={cellClass(has('spenderId'))}
          >
            <option value="">선택</option>
            {userOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </td>

        <td className="px-2 py-1.5">
          <input
            type="text"
            value={row.memo}
            onChange={(e) => set({ memo: e.target.value })}
            disabled={disabled}
            placeholder="메모"
            aria-label={`${rowNumber}행 메모`}
            className={CONTROL_CLASS}
          />
        </td>

        <td className="px-1 py-1.5">
          {/*
            "삭제" 글자 대신 ×를 쓴다. 표에서 이 열이 차지하는 폭이
            그대로 메모 칸에서 빠지기 때문. 의미는 aria-label로 유지한다.
          */}
          <button
            type="button"
            onClick={() => onRemove(row.clientId)}
            disabled={disabled}
            aria-label={`${rowNumber}행 삭제`}
            title="행 삭제"
            className="w-8 h-9 text-lg leading-none text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
          >
            ×
          </button>
        </td>
      </tr>

      {/*
        오류 문구는 칸 옆이 아니라 행 아래 한 줄로 모은다.
        표의 칸은 좁아서 문구를 넣으면 열 너비가 들쭉날쭉해지고, 어차피 어느 칸이
        문제인지는 위에서 빨간 테두리로 보인다.
      */}
      {errors.length > 0 && (
        <tr className="bg-red-50/60 dark:bg-red-900/10">
          <td />
          <td colSpan={9} className="px-2 pb-2">
            <p className="text-xs text-red-600 dark:text-red-400" role="alert">
              {errors.map((e) => e.message).join(' · ')}
            </p>
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * @why 최대 50행이 동시에 존재한다. memo가 없으면 한 칸을 고칠 때마다
 * 모든 행이 다시 그려져 입력이 밀린다.
 */
const BulkTransactionTableRow = memo(BulkTransactionTableRowBase)

export interface BulkTransactionTableProps {
  rows: BulkTransactionDraftRow[]
  /** 행별 표현 props를 만들어 주는 함수 (페이지가 소유한 선택지·검증 결과를 주입) */
  renderProps: (
    row: BulkTransactionDraftRow,
    index: number
  ) => BulkRowViewProps
}

export function BulkTransactionTable({ rows, renderProps }: BulkTransactionTableProps) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      {/*
        열 폭 배분 원칙: 고정 폭은 각 컨트롤에 필요한 최소치만 주고,
        남는 폭은 전부 메모가 흡수하게 둔다(메모만 폭 지정 없음).
        메모는 자유 입력이라 넓을수록 쓸모가 커지는 유일한 열이다.
      */}
      <table className="w-full min-w-[820px] border-collapse">
        <thead className="bg-gray-50 dark:bg-gray-800/60">
          <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400">
            <th className="px-2 py-2 w-8">#</th>
            <th className="px-2 py-2 w-[124px]">날짜</th>
            <th className="px-2 py-2 w-20">구분</th>
            <th className="px-2 py-2 w-24">금액</th>
            <th className="px-2 py-2 w-28">대분류</th>
            <th className="px-2 py-2 w-28">소분류</th>
            <th className="px-2 py-2 w-28">결제수단</th>
            <th className="px-2 py-2 w-28">사람</th>
            <th className="px-2 py-2">메모</th>
            <th className="px-1 py-2 w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((row, index) => (
            // key는 배열 인덱스가 아닌 clientId. 인덱스를 쓰면 행 삭제 시
            // 입력 중이던 칸의 포커스와 한글 조합 상태가 다른 행으로 옮겨간다.
            <BulkTransactionTableRow key={row.clientId} {...renderProps(row, index)} />
          ))}
        </tbody>
      </table>
    </div>
  )
}
